/**
 * ShortenDialog, sheet 43 callout 3 (mission 10 WP16 T091; FR-017, FR-024, NFR-011; D.2, A.5; SC-004's screen
 * half, US3 scenario 2; spec amendment 26; style guide §7.7, §7.13, §9; contracts/retention-api.md refusal 6).
 *
 * Opened by the 409 `retention-confirmation-required` (`useRetentionMutation().confirmation`):
 * - `shortenCount` states the new window (`days`, counsel memo 2026-10-03 C2) and `due_count`, the exact N, never
 *   the listed count; at most 100 runs follow, each with
 *   its mono short id and the completion, current and new deletion dates in UTC `<time datetime>`; when
 *   `truncated`, `shortenTruncated` names the remainder (N less the listed runs) and points at the runs table;
 * - Confirm re-sends the change with `confirm_due_count` set to that N. A second 409 (a run completed or was
 *   swept in between) replaces the list in place; nothing is deleted that a confirmation did not count;
 * - non-blocking (§7.13): ×, Cancel and Esc close it and send nothing more; Radix traps focus; the page opens it
 *   without a trigger, so `onReturnFocus` puts focus back on Save.
 */
import { X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Mono } from '@/lib/format'
import { PROVISIONAL_COPY, copyText } from '@/lib/provisional-copy'
import type { RetentionConfirmation } from '@/lib/queries'

import { UtcDate } from './dates'

const HEADINGS = [
  'shortenRunHeadings.run',
  'shortenRunHeadings.completed',
  'shortenRunHeadings.currentDue',
  'shortenRunHeadings.newDue',
] as const

export interface ShortenDialogProps {
  open: boolean
  confirmation: RetentionConfirmation | null
  /** The window the refused change asks for, in days (counsel C2 names it). */
  days: number
  /** `terms.sweep_interval_minutes`: deletion lands within one sweep of saving. */
  minutes: number
  pending: boolean
  /** The confirmation's extra member, merged into the re-sent PATCH body. */
  onConfirm: (body: { confirm_due_count: number }) => void
  onCancel: () => void
  onReturnFocus: () => void
}

export function ShortenDialog({ open, confirmation, days, minutes, pending, onConfirm, onCancel, onReturnFocus }: ShortenDialogProps) {
  return (
    <Dialog open={open && confirmation !== null} onOpenChange={(next) => (next ? undefined : onCancel())}>
      <DialogContent
        size="lg"
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          onReturnFocus()
        }}
      >
        {confirmation === null ? null : (
          <>
            <DialogHeader>
              <DialogTitle>{copyText('shortenTitle')}</DialogTitle>
              <DialogClose asChild>
                <Button variant="ghost" size="icon" aria-label={copyText('confirmationClose')}>
                  <X aria-hidden />
                </Button>
              </DialogClose>
            </DialogHeader>
            <DialogBody className="flex flex-col gap-4">
              <DialogDescription className="text-body text-navy-950">
                {PROVISIONAL_COPY.shortenCount.render({
                  days,
                  count: confirmation.due_count,
                  minutes,
                })}
              </DialogDescription>
              <div className="max-h-[320px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow dense>
                      {HEADINGS.map((key) => (
                        <TableHead key={key}>{copyText(key)}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {confirmation.runs.map((run, i) => (
                      <TableRow key={`${i}:${run.short_id}`} dense>
                        <TableCell>
                          <Mono className="text-body-sm">{run.short_id}</Mono>
                        </TableCell>
                        {/* Mission 10 WP17 (WP07 review): a null completion is blank; a null current due date waits on the
                            first transmission (U26). Neither is invented. */}
                        <TableCell className="text-body-sm">
                          {run.completed_at === null ? null : <UtcDate iso={run.completed_at} mono />}
                        </TableCell>
                        <TableCell className="text-body-sm">
                          {run.current_due_at === null ? copyText('runsWaitingForTransmission') : <UtcDate iso={run.current_due_at} mono />}
                        </TableCell>
                        <TableCell className="text-body-sm">
                          <UtcDate iso={run.new_due_at} mono />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {confirmation.truncated ? (
                <p className="text-body-sm text-navy-700">
                  {PROVISIONAL_COPY.shortenTruncated.render({
                    more: confirmation.due_count - confirmation.runs.length,
                  })}
                </p>
              ) : null}
            </DialogBody>
            <DialogFooter>
              <Button variant="ghost" onClick={onCancel}>
                {copyText('modalCancel')}
              </Button>
              <Button variant="destructive" loading={pending} onClick={() => onConfirm({ confirm_due_count: confirmation.due_count })}>
                {copyText('shortenConfirm')}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
